import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Circle, Sparkles } from 'lucide-react';

/**
 * Sidebar-mounted Quick Start widget for BETA members.
 * Renders inside DashboardLayout right above the Credit Balance block.
 *
 *  • Always-on pulse/glow (constant breathing animation) while incomplete
 *  • Collapses when the sidebar is collapsed (shows just the icon + dot count)
 *  • Auto-hides forever when all 4 items are complete (localStorage sticky)
 *  • Clicking an incomplete item navigates to the corresponding section
 */
export default function QuickStartChecklist({ collapsed = false, isDarkMode = true }) {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);

  const skipOnPath = /^\/(login|signup|set-password|beta-onboarding|billing-setup)/i.test(location.pathname || '');

  useEffect(() => {
    if (typeof window !== 'undefined' && localStorage.getItem('qs_checklist_hidden') === '1') setHidden(true);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!user || skipOnPath) return;
    const run = async () => {
      try {
        const res = await api.get('/api/me/beta-status');
        if (!cancelled) setData(res.data);
      } catch { /* ignore */ }
    };
    run();
    const iv = setInterval(run, 15 * 60 * 1000);
    return () => { cancelled = true; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id, location.pathname]);

  if (!data?.is_beta || data?.is_lifetime || hidden) return null;

  const cl = data?.checklist || {};
  const wsId = (typeof window !== 'undefined') ? localStorage.getItem('currentWorkspaceId') : null;

  const items = [
    { key: 'has_subaccount', label: 'Create sub-account', done: !!cl.has_subaccount, to: `/workspace/${wsId}/sub-accounts` },
    { key: 'has_balance',    label: 'Add balance',        done: !!cl.has_balance,    to: `/workspace/${wsId}/billing` },
    { key: 'has_rebilling',  label: 'Set re-billing',     done: !!cl.has_rebilling,  to: `/workspace/${wsId}/billing` },
    { key: 'has_agent',      label: 'Create first agent', done: !!cl.has_agent,      to: `/workspace/${wsId}/sub-accounts` },
    { key: 'has_feedback',   label: 'Send feedback',      done: !!cl.has_feedback,   action: 'open-feedback' },
  ];
  const completed = items.filter(i => i.done).length;

  if (completed === items.length) {
    if (typeof window !== 'undefined') localStorage.setItem('qs_checklist_hidden', '1');
    return null;
  }

  const handleItemClick = (it) => {
    if (it.done) return;
    if (it.action === 'open-feedback') {
      if (typeof window !== 'undefined') window.dispatchEvent(new Event('open-feedback-modal'));
      return;
    }
    if (wsId && it.to) navigate(it.to);
  };

  // Collapsed sidebar: compact pill only
  if (collapsed) {
    return (
      <motion.div
        animate={{
          boxShadow: [
            '0 0 0 0 rgba(45,212,191,0.0)',
            '0 0 0 3px rgba(45,212,191,0.28)',
            '0 0 16px 2px rgba(45,212,191,0.22)',
            '0 0 0 0 rgba(45,212,191,0.0)',
          ],
        }}
        transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
        className={`mx-auto w-10 h-10 flex items-center justify-center rounded-xl border cursor-pointer ${isDarkMode ? 'bg-teal-400/[0.08] border-teal-400/30 text-teal-300' : 'bg-teal-50 border-teal-200 text-teal-700'}`}
        onClick={() => {
          const next = items.find(i => !i.done);
          if (next) handleItemClick(next);
        }}
        title={`Quick Start — ${completed} of ${items.length} complete`}
        data-testid="quickstart-checklist"
      >
        <Sparkles size={16} />
      </motion.div>
    );
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{
          opacity: 1,
          y: 0,
          boxShadow: [
            '0 0 0 0 rgba(45,212,191,0)',
            '0 0 0 2px rgba(45,212,191,0.25)',
            '0 0 22px 4px rgba(45,212,191,0.18)',
            '0 0 0 0 rgba(45,212,191,0)',
          ],
        }}
        transition={{
          y: { duration: 0.3 },
          boxShadow: { duration: 2.6, repeat: Infinity, ease: 'easeInOut' },
        }}
        className={`rounded-xl border overflow-hidden ${isDarkMode ? 'bg-gradient-to-b from-teal-400/[0.05] to-teal-400/[0.02] border-teal-400/25' : 'bg-gradient-to-b from-teal-50 to-white border-teal-200'}`}
        data-testid="quickstart-checklist"
      >
        <div className="px-3 py-2.5 flex items-center gap-2">
          <motion.div
            animate={{ scale: [1, 1.08, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: 'linear-gradient(135deg,#2dd4bf,#22d3ee)' }}
          >
            <Sparkles className="text-[#0a0b0f]" size={13} />
          </motion.div>
          <div className="flex-1 min-w-0">
            <p className={`text-[11px] uppercase tracking-wider font-semibold ${isDarkMode ? 'text-teal-300/80' : 'text-teal-700'}`}>Quick Start</p>
            <p className={`text-[10.5px] ${isDarkMode ? 'text-slate-400' : 'text-gray-500'} tracking-wide`}>{completed} of {items.length} complete</p>
          </div>
        </div>

        <div className={`h-1 ${isDarkMode ? 'bg-white/[0.05]' : 'bg-gray-100'}`}>
          <div className="h-1 bg-gradient-to-r from-teal-400 to-cyan-400 transition-all"
               style={{ width: `${(completed / items.length) * 100}%` }} />
        </div>

        <div className="p-1.5">
          {items.map((it) => (
            <button
              key={it.key}
              onClick={() => handleItemClick(it)}
              disabled={it.done}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left transition-colors ${
                it.done ? 'opacity-60' : `${isDarkMode ? 'hover:bg-white/[0.04]' : 'hover:bg-gray-100'}`
              }`}
              data-testid={`quickstart-item-${it.key}`}
            >
              {it.done ? (
                <CheckCircle2 className="text-teal-400 flex-shrink-0" size={13} />
              ) : (
                <Circle className={`${isDarkMode ? 'text-slate-500' : 'text-gray-400'} flex-shrink-0`} size={13} />
              )}
              <span className={`text-[11.5px] truncate ${
                it.done
                  ? `${isDarkMode ? 'text-slate-500' : 'text-gray-400'} line-through`
                  : `${isDarkMode ? 'text-slate-100' : 'text-gray-800'}`
              }`}>
                {it.label}
              </span>
            </button>
          ))}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
